import {AxiosResponse} from "axios";
import React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import Form from "../../../../common/components/shared/form/form";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {storesContext} from "../../../../common/stores/stores";
import {Api} from "../../../../helpers/api";
import {routingAddresses} from "../../../../helpers/routing";
import {Utils} from "../../../../helpers/utils";
import i18n from "../../../../i18n/i18n";
import {NewTicketModel} from "../../../models/ticket";
import {ITicketStore} from "../../../stores/ticketStore";
import CargoSection from "./cargoSection/cargoSection";
import DangerousGoodsSection from "../../../components/dangerousGoodsSection/dangerousGoodsSection";
import GeneralInformationSection from "./generalInformationSection/generalInformationSection";
import "../../transportation.scss";
import TransportationSection from "./transportationSection/transportationSection";
import VoyageSection from "./voyageSection/voyageSection";
import {TicketService} from "../../../services/ticketService";
import {DateUtils} from "../../../../helpers/dateUtils";
import {observer} from "mobx-react";
import TransportUtils from "../../../../helpers/transportUtils";
import {RelationType} from "../../../models/relation";

interface TicketFormState {
    submitButtonWasClicked: boolean;
    isSubmitButtonBlocked: boolean;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

interface TicketFormProps extends RouteComponentProps {
    id?: string;
    isUpdate: boolean;
}

class TicketForm extends React.Component <TicketFormProps, TicketFormState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            submitButtonWasClicked: false,
            isSubmitButtonBlocked: false,
            response: undefined
        };
    }

    componentWillUnmount() {
        this.context.ticketStore.clearStore();
    }

    render(): JSX.Element {
        return (
            <Form
                class="transportation-ticket-form"
                renderContent={this.renderContent()}
                renderError={this.renderErrors()}
                submit={this.submit.bind(this)}
                backAddress={routingAddresses.ticket.list}
                isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                saveButtonText={this.props.isUpdate ? i18n.t("save") : i18n.t("transportation.ticket.addTicket.saveButtonText")}
            />
        );
    }

    private renderErrors() {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={this.getErrorMessage()}
                    translatedFields={{
                        status: i18n.t("transportation.ticket.httpErrors.status"),
                        cargoType: i18n.t("transportation.ticket.httpErrors.cargoType"),
                        gateNo: i18n.t("transportation.ticket.httpErrors.gateNo"),
                        currentRoute: i18n.t("transportation.ticket.httpErrors.currentRoute"),
                        trailerRegNo: i18n.t("transportation.ticket.httpErrors.trailerRegNo"),
                        transportCompany: i18n.t("transportation.ticket.httpErrors.transportCompany"),
                        lorryRegNo: i18n.t("transportation.ticket.httpErrors.lorryRegNo"),
                        shipownerName: i18n.t("transportation.ticket.httpErrors.shipownerName"),
                        voyageNo: i18n.t("transportation.ticket.httpErrors.voyageNo"),
                        arrivalDepartureTime: i18n.t("transportation.ticket.httpErrors.arrivalDepartureTime"),
                        ferryName: i18n.t("transportation.ticket.httpErrors.ferryName"),
                        originDestinationPort: i18n.t("transportation.ticket.httpErrors.originDestinationPort"),
                        originDestinationCountry: i18n.t("transportation.ticket.httpErrors.originDestinationCountry"),
                        cargoDesc: i18n.t("transportation.ticket.httpErrors.cargoDesc"),
                        weight: i18n.t("transportation.ticket.httpErrors.weight"),
                        length: i18n.t("transportation.ticket.httpErrors.length"),
                        width: i18n.t("transportation.ticket.httpErrors.width"),
                        securingDeclaration: i18n.t("transportation.ticket.httpErrors.securingDeclaration"),
                        unCode: i18n.t("transportation.ticket.httpErrors.unCode"),
                        packing: i18n.t("transportation.ticket.httpErrors.packing"),
                        packingQuantity: i18n.t("transportation.ticket.httpErrors.packingQuantity"),
                        packingGroup: i18n.t("transportation.ticket.httpErrors.packingGroup"),
                        dangerousGoodsWeight: i18n.t("transportation.ticket.httpErrors.dangerousGoodsWeight")
                    }}
                    translatedFieldsForEmpty={{
                        cargoTypeEmpty: i18n.t("transportation.ticket.httpErrors.cargoTypeEmpty"),
                        gateNoEmpty: i18n.t("transportation.ticket.httpErrors.gateNoEmpty"),
                        currentRouteEmpty: i18n.t("transportation.ticket.httpErrors.currentRouteEmpty"),
                        trailerRegNoEmpty: i18n.t("transportation.ticket.httpErrors.trailerRegNoEmpty"),
                        shipownerNameEmpty: i18n.t("transportation.ticket.httpErrors.shipownerNameEmpty"),
                        voyageNoEmpty: i18n.t("transportation.ticket.httpErrors.voyageNoEmpty"),
                        arrivalDepartureTimeEmpty: i18n.t("transportation.ticket.httpErrors.arrivalDepartureTimeEmpty"),
                        ferryNameEmpty: i18n.t("transportation.ticket.httpErrors.ferryNameEmpty"),
                        originDestinationPortEmpty: i18n.t("transportation.ticket.httpErrors.originDestinationPortEmpty"),
                        originDestinationCountryEmpty: i18n.t("transportation.ticket.httpErrors.originDestinationCountryEmpty"),
                        weightEmpty: i18n.t("transportation.ticket.httpErrors.weightEmpty"),
                        lengthEmpty: i18n.t("transportation.ticket.httpErrors.lengthEmpty"),
                        widthEmpty: i18n.t("transportation.ticket.httpErrors.widthEmpty"),
                        unCodeEmpty: i18n.t("transportation.ticket.httpErrors.unCodeEmpty")
                    }}
                    object={i18n.t("transportation.ticket.httpErrors.object")}
                />
            );
        }
    }

    private renderContent() {
        return (
            <>
                <GeneralInformationSection isUpdate={this.props.isUpdate} ticketId={this.props.id}/>
                <TransportationSection/>
                <VoyageSection/>
                <CargoSection/>
                <DangerousGoodsSection store={this.context.ticketStore as ITicketStore}/>
            </>
        );
    }

    private getErrorMessage() {
        return this.props.isUpdate
            ? i18n.t("transportation.ticket.errors.modifyErrorSuffix")
            : i18n.t("transportation.ticket.errors.addErrorSuffix");
    }

    private get isValid() {
        const ticketStore = this.context.ticketStore as ITicketStore;
        return ticketStore.ticketIsValid();
    }

    private async submit() {
        this.setState({submitButtonWasClicked: true, response: undefined});

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});

            let response: AxiosResponse<HttpErrorModel[] | any>;

            if (this.props.isUpdate && this.props.id) {
                response = await TicketService.modifyTicket(this.prepareDataToApiForTicket(), this.props.id);
            } else {
                response = await TicketService.addTicket(this.prepareDataToApiForTicket());
            }
            this.setState({isSubmitButtonBlocked: false, response});

            if (Api.isSuccess(this.state.response)) {
                this.props.history.push(routingAddresses.ticket.list);
            }
        }
        Utils.scrollToFirstErrorMessage();
    }

    private prepareDataToApiForTicket(): NewTicketModel {
        const ticketStore = this.context.ticketStore as ITicketStore;

        return {
            status: ticketStore.status.value,
            cargoType: ticketStore.cargoType.value,
            currentRoute: ticketStore.currentRoute.value || undefined,
            barcodeNo: ticketStore.barcodeNo.value,
            gateNo: ticketStore.gateNo.value || undefined,
            trailerRegNo: ticketStore.trailerRegNo.value.trim() || undefined,
            transportCompanyId: ticketStore.transportCompanyId.value,
            lorryRegNo: ticketStore.lorryRegNo.value,
            shipownerId: ticketStore.shipownerId.value,
            voyageNo: ticketStore.voyageNo.value.trim(),
            arrivalDepartureTime: DateUtils.getFormattedDateWithTimeInUTC(ticketStore.arrivalDepartureTime.value),
            ferryId: ticketStore.ferryId.value,
            originDestinationPortId: ticketStore.originDestinationPortId.value,
            originDestinationCountry: ticketStore.originDestinationCountry.value,
            bookingNo: ticketStore.bookingNo.value.trim(),
            cargoDesc: ticketStore.cargoDesc.value,
            weight: ticketStore.weight.value || null,
            height: ticketStore.height.value || null,
            length: ticketStore.length.value || null,
            width: ticketStore.width.value || null,
            securingDeclaration: ticketStore.securingDeclaration.value,
            dangerousGoods: TransportUtils.prepareDangerousGoods(ticketStore.dangerousGoods),
            relationType: RelationType.TICKET_EXPORT
        };
    }
}

export default withRouter(observer(TicketForm));
